import { useQuery } from "@tanstack/react-query";
import { getCoreRowModel, getSortedRowModel, type SortingState, useReactTable } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import Table from "~/components/Table";
import type { BillingSettings } from "~/modules/billing/api/billingSettings/billingSettingsTypes.ts";
import { useBillingSettingsLookups } from "~/modules/billing/hooks/useBillingSettingsLookups.ts";
import { orderQueries } from "~/modules/project/api/order/orderQueries.ts";
import { useProjectsProjectPhases } from "~/modules/project/api/projectPhase/projectPhaseQueries.ts";

import { divergingOrderBillingSettingsTableColumnDefs } from "./divergingOrderBillingSettingsTableColumnDefs.tsx";
import {
	buildDivergingOrderBillingSettingsTableData,
	computeColumnVisibility,
} from "./divergingOrderBillingSettingsTableUtils.ts";

type DivergingOrderBillingSettingsTableProps = {
	projectBillingSettings: BillingSettings;
	projectId: string;
	clientId: string;
};

export function DivergingOrderBillingSettingsTable({
	projectBillingSettings,
	projectId,
	clientId,
}: DivergingOrderBillingSettingsTableProps) {
	const { data: orders, isLoading: ordersLoading } = useQuery(orderQueries.byProject(projectId));
	const { data: phases, isLoading: phasesLoading } = useProjectsProjectPhases(projectId);
	const {
		currencies,
		invoiceRecipients,
		clientContactPersons,
		isLoading: lookupsIsLoading,
	} = useBillingSettingsLookups(clientId);

	const isLoading = ordersLoading || phasesLoading || lookupsIsLoading;

	const { rows: tableData, columnVisibility } = useMemo(() => {
		if (!orders || !phases) return { rows: [], columnVisibility: {} };

		const rows = buildDivergingOrderBillingSettingsTableData(
			orders,
			phases,
			projectBillingSettings,
			{ currencies, invoiceRecipients, clientContactPersons },
			clientId,
		);

		return {
			rows,
			columnVisibility: computeColumnVisibility(rows),
		};
	}, [orders, phases, projectBillingSettings, currencies, invoiceRecipients, clientContactPersons, clientId]);

	const [sorting, setSorting] = useState<SortingState>([]);

	const table = useReactTable({
		data: tableData,
		columns: divergingOrderBillingSettingsTableColumnDefs,
		state: { sorting, columnVisibility },
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getRowId: (row) => row.orderId,
	});

	if (isLoading) {
		return null;
	}

	if (tableData.length === 0) {
		return "Keine abweichenden Abrechnungsstellungseinstellungen vorhanden.";
	}

	return (
		<Table table={table} />
	);
}